import PropTypes from 'prop-types';
import { Box, Card, CardContent, Table, TableBody, TableCell, TableHead, TableRow, Typography, CircularProgress } from '@mui/material';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';

/**
 * SimilarDaysChart Component
 * 
 * Displays table of historically similar trading days and their forward returns.
 * Data comes from /api/behavior/similar-days/{date}.
 * 
 * Props:
 *   - similarDays: Array of SimilarHistoricalDayResponse from API
 *   - loading: Boolean indicating data fetch state
 *   - t: Translation function
 */
const SimilarDaysChart = ({ similarDays, loading, t }) => {
  if (loading) {
    return (
      <Card sx={{ mb: 3 }}>
        <CardContent sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 200 }}>
          <CircularProgress />
        </CardContent>
      </Card>
    );
  }

  if (!similarDays || similarDays.length === 0) {
    return (
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography color="textSecondary">
            {t('sentiment.behavior.similar_days.empty', 'No similar historical days found')}
          </Typography>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card sx={{ mb: 3 }}>
      <CardContent>
        <Typography variant="h6" sx={{ fontWeight: 700, mb: 2 }}>
          {t('sentiment.behavior.similar_days.title', 'Historical Similar Days')}
        </Typography>
        <Box sx={{ overflowX: 'auto' }}>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ backgroundColor: 'rgba(0,0,0,0.05)' }}>
                <TableCell sx={{ fontWeight: 700 }}>
                  {t('sentiment.behavior.similar_days.date', 'Date')}
                </TableCell>
                <TableCell align="right" sx={{ fontWeight: 700 }}>
                  {t('sentiment.behavior.similar_days.distance', 'Distance')}
                </TableCell>
                <TableCell align="right" sx={{ fontWeight: 700 }}>
                  {t('sentiment.behavior.similar_days.panic', 'Panic')}
                </TableCell>
                <TableCell align="right" sx={{ fontWeight: 700 }}>
                  {t('sentiment.behavior.similar_days.fomo', 'FOMO')}
                </TableCell>
                <TableCell align="right" sx={{ fontWeight: 700 }}>
                  {t('sentiment.behavior.similar_days.fwd_20d', 'Fwd 20d')}
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {similarDays.map((day, idx) => {
                const fwd_return = day.fwd_return_20d;
                const is_positive = fwd_return != null && fwd_return > 0;

                return (
                  <TableRow key={`${day.similar_date}-${idx}`} hover>
                    <TableCell>{day.similar_date}</TableCell>
                    <TableCell align="right">
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {day.distance.toFixed(3)}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      {day.panic_score != null && (
                        <Typography
                          variant="body2"
                          sx={{
                            fontWeight: 600,
                            color: day.panic_score >= 0.80 ? '#d32f2f' : 'inherit',
                          }}
                        >
                          {(day.panic_score * 100).toFixed(0)}%
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell align="right">
                      {day.fomo_score != null && (
                        <Typography
                          variant="body2"
                          sx={{
                            fontWeight: 600,
                            color: day.fomo_score >= 0.80 ? '#d32f2f' : 'inherit',
                          }}
                        >
                          {(day.fomo_score * 100).toFixed(0)}%
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell align="right">
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 0.5 }}>
                        {fwd_return != null && (
                          <>
                            {is_positive ? (
                              <TrendingUpIcon sx={{ color: '#388e3c', fontSize: 18 }} />
                            ) : (
                              <TrendingDownIcon sx={{ color: '#d32f2f', fontSize: 18 }} />
                            )}
                            <Typography
                              variant="body2"
                              sx={{
                                fontWeight: 700,
                                color: is_positive ? '#388e3c' : '#d32f2f',
                              }}
                            >
                              {(fwd_return * 100).toFixed(2)}%
                            </Typography>
                          </>
                        )}
                      </Box>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </Box>
        <Typography
          variant="caption"
          sx={{ display: 'block', mt: 1.5, opacity: 0.6 }}
        >
          {t('sentiment.behavior.similar_days.desc',
            'Distance: Feature space similarity (lower = closer match). Fwd 20d: 20-day forward return from similar day.')}
        </Typography>
      </CardContent>
    </Card>
  );
};

SimilarDaysChart.propTypes = {
  similarDays: PropTypes.arrayOf(PropTypes.shape({
    similar_date: PropTypes.string.isRequired,
    distance: PropTypes.number.isRequired,
    panic_score: PropTypes.number,
    fomo_score: PropTypes.number,
    fwd_return_20d: PropTypes.number,
  })),
  loading: PropTypes.bool,
  t: PropTypes.func.isRequired,
};

SimilarDaysChart.defaultProps = {
  similarDays: [],
  loading: false,
};

export default SimilarDaysChart;
